import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { authFileClick } from "@/lib/openFile";
import Layout from "@/components/Layout";
import { ArrowLeft, PaperPlaneTilt, UploadSimple, FileText, DownloadSimple, CheckCircle, Image as ImageIcon } from "@phosphor-icons/react";
import AuthImage from "@/components/AuthImage";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { formatDate, formatDateTime, formatCurrency } from "@/lib/format";
import { useCategories } from "@/hooks/useCategories";
import { toast } from "sonner";

const STATUS_META = {
  PENDING_REVIEW: { label: "Pending review", cls: "bg-zinc-100 text-zinc-800" },
  IN_REVIEW: { label: "In review", cls: "bg-amber-50 text-amber-800" },
  QUOTED: { label: "Quoted", cls: "bg-[#FF5A1F]/10 text-[#E04814]" },
  APPROVED: { label: "Approved", cls: "bg-green-50 text-green-800" },
  REJECTED: { label: "Rejected", cls: "bg-red-50 text-red-800" },
};

export default function AdminQuotationDetail() {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const { labelFor } = useCategories();
  const [q, setQ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const fileRef = useRef(null);

  const fetchQ = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/quotations/${quotationId}`);
      setQ(res.data);
      if (res.data.quote_amount != null) setAmount(String(res.data.quote_amount));
      if (res.data.quote_notes) setNotes(res.data.quote_notes);
      if (res.data.quote_valid_until) setValidUntil(res.data.quote_valid_until.slice(0, 10));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [quotationId]);

  useEffect(() => { fetchQ(); }, [fetchQ]);

  const sendQuote = async () => {
    if (!amount) return toast.error("Enter a quote amount");
    setBusy(true);
    try {
      await api.patch(`/quotations/${quotationId}/quote`, {
        quote_amount: Number(amount),
        quote_notes: notes,
        quote_valid_until: validUntil || null,
      });
      toast.success("Quote sent to client");
      fetchQ();
    } catch (e) {
      toast.error("Failed to send");
    }
    setBusy(false);
  };

  const uploadCert = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("kind", "certificate");
    form.append("file", f);
    try {
      await api.post(`/quotations/${quotationId}/files`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File attached");
      if (fileRef.current) fileRef.current.value = "";
      fetchQ();
    } catch (err) { toast.error("Upload failed"); }
  };

  if (loading || !q) {
    return <Layout><div className="h-96 bg-white border border-zinc-200 animate-pulse" /></Layout>;
  }

  const meta = STATUS_META[q.status] || STATUS_META.PENDING_REVIEW;
  const canQuote = q.status === "PENDING_REVIEW" || q.status === "IN_REVIEW" || q.status === "QUOTED";

  return (
    <Layout>
      <Link to="/admin/quotations" data-testid="admin-quote-back" className="inline-flex items-center gap-2 mt-tiny text-zinc-500 hover:text-zinc-900 mb-6">
        <ArrowLeft size={14} /> Back to quotations
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: request info */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white border border-zinc-200 p-6 md:p-8">
            <div className="flex flex-wrap items-baseline gap-3">
              <div className="mt-tiny text-zinc-400 font-mono-mt">{q.quotation_number}</div>
              <span className={`inline-flex items-center px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.15em] ${meta.cls}`}>{meta.label}</span>
            </div>
            <h1 className="font-display text-3xl font-black text-zinc-900 mt-2">{q.material}</h1>
            <div className="mt-1 text-sm text-zinc-500">{labelFor(q.category)} · {formatDateTime(q.created_at)}</div>

            <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
              <Cell label="Client" value={q.client_name} />
              <Cell label="Email" value={q.client_email} mono />
              {q.client_company && <Cell label="Company" value={q.client_company} />}
              {q.quantity && <Cell label="Quantity" value={q.quantity} />}
              {q.dimensions && <Cell label="Dimensions" value={q.dimensions} />}
              {q.project_name && <Cell label="Project" value={q.project_name} />}
            </div>

            {q.specifications && Object.keys(q.specifications).length > 0 && (
              <div className="mt-6" data-testid="admin-quotation-specifications">
                <div className="mt-tiny text-zinc-400 mb-2">Client selection</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(q.specifications).map(([k, v]) => (
                    <span key={k} className="inline-flex items-center gap-1.5 border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-sm">
                      <span className="text-zinc-500 text-xs uppercase tracking-wide">{k}</span>
                      <b className="text-zinc-900">{String(v)}</b>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {q.notes && (
              <div className="mt-6 border-l-2 border-zinc-300 pl-4 py-1">
                <div className="mt-tiny text-zinc-400">Client notes</div>
                <div className="mt-1 text-sm text-zinc-700">{q.notes}</div>
              </div>
            )}
          </div>

          {(q.approved_at || q.accepted_at) && (
            <div className="border border-green-200 bg-green-50 p-4 flex items-start gap-3">
              <CheckCircle size={22} weight="fill" className="text-green-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-green-900">Approved by client</div>
                <div className="mt-tiny text-green-700">{formatDateTime(q.approved_at || q.accepted_at)}</div>
                {q.order_number && (
                  <Link
                    to={q.order_id ? `/admin/orders/${q.order_id}` : "/admin"}
                    data-testid="admin-view-generated-order-link"
                    className="mt-2 inline-flex items-center gap-1 text-[#0047AB] hover:underline text-sm font-medium"
                  >
                    → Track order {q.order_number}
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right: quote form + attachments */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white border border-zinc-200 p-6 md:p-8">
            <div className="mt-tiny text-[#FF5A1F] mb-3">Send quote</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="mt-tiny text-zinc-500 mb-1.5">Amount (AED) *</div>
                <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="h-11 rounded-none border-zinc-200" data-testid="admin-quote-amount-input" placeholder="12500.00" />
              </div>
              <div>
                <div className="mt-tiny text-zinc-500 mb-1.5">Valid until</div>
                <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)}
                  className="h-11 rounded-none border-zinc-200" data-testid="admin-quote-valid-input" />
              </div>
            </div>
            <div className="mt-4">
              <div className="mt-tiny text-zinc-500 mb-1.5">Notes for the client</div>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                className="rounded-none border-zinc-200 min-h-[100px]" data-testid="admin-quote-notes-input"
                placeholder="Delivery in 14 days, includes on-site installation..." />
            </div>
            <button
              disabled={busy || !canQuote}
              onClick={sendQuote}
              data-testid="admin-quote-send-button"
              className="mt-5 inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white px-6 py-3 font-medium transition-colors disabled:opacity-60"
            >
              <PaperPlaneTilt size={18} weight="fill" /> {busy ? "Sending..." : q.status === "QUOTED" ? "Resend / update quote" : "Send quote"}
            </button>
          </div>

          <div className="bg-white border border-zinc-200 p-6 md:p-8" data-testid="admin-client-attachments">
            <div className="mt-tiny text-[#0047AB] mb-1">Client site drawings & photos</div>
            <p className="text-xs text-zinc-500 mb-3">Uploaded by the client with this request — reference while pricing.</p>
            {(() => {
              const siteFiles = (q.files || []).filter((f) => f.kind === "site_drawing");
              if (siteFiles.length === 0) {
                return <div className="mt-tiny text-zinc-400">No site files uploaded by the client.</div>;
              }
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {siteFiles.map((f) => {
                    const isImg = (f.content_type || "").startsWith("image/");
                    return (
                      <a
                        key={f.file_id}
                        href={`${API}/files/${f.storage_path}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={authFileClick(f.storage_path)}
                        data-testid={`client-attachment-${f.file_id}`}
                        className="group border border-zinc-200 hover:border-[#0047AB] transition-colors overflow-hidden"
                      >
                        <div className="aspect-square bg-zinc-50 grid place-items-center overflow-hidden">
                          {isImg ? (
                            <AuthImage path={f.storage_path} alt={f.original_filename} className="w-full h-full object-cover" />
                          ) : (
                            <FileText size={34} className="text-[#B22222]" weight="duotone" />
                          )}
                        </div>
                        <div className="p-2 flex items-center justify-between gap-2">
                          <span className="text-xs text-zinc-700 truncate">{f.original_filename}</span>
                          <DownloadSimple size={15} className="text-zinc-400 shrink-0 group-hover:text-[#0047AB]" />
                        </div>
                      </a>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          <div className="bg-white border border-zinc-200 p-6 md:p-8">
            <div className="mt-tiny text-[#FF5A1F] mb-3">Attach certificates & docs</div>
            <label data-testid="admin-quote-upload-label" className="block border border-dashed border-zinc-300 hover:border-[#0047AB] hover:bg-zinc-50 p-4 flex items-center justify-center gap-3 cursor-pointer transition-colors">
              <UploadSimple size={20} className="text-[#0047AB]" />
              <span className="text-sm font-medium text-zinc-700">Attach PDF (certificate, spec sheet, etc.)</span>
              <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={uploadCert} className="hidden" data-testid="admin-quote-upload-input" />
            </label>
            <div className="mt-4 grid gap-2">
              {(q.files || []).filter((f) => f.kind !== "site_drawing").length === 0 ? (
                <div className="mt-tiny text-zinc-400">No files attached yet.</div>
              ) : (q.files || []).filter((f) => f.kind !== "site_drawing").map((f) => (
                <a key={f.file_id} href={`${API}/files/${f.storage_path}`} target="_blank" rel="noreferrer" onClick={authFileClick(f.storage_path)}
                  className="flex items-center justify-between gap-3 border border-zinc-200 hover:border-zinc-900 hover:bg-zinc-50 p-3 transition-colors">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={18} className="text-[#0047AB] shrink-0" weight="duotone" />
                    <div className="min-w-0 text-sm text-zinc-900 truncate">{f.original_filename}</div>
                  </div>
                  <DownloadSimple size={18} className="text-zinc-400" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Cell({ label, value, mono }) {
  return (
    <div>
      <div className="mt-tiny text-zinc-400">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono-mt text-zinc-900" : "text-zinc-900 font-medium"}`}>{value}</div>
    </div>
  );
}
